import { Howler } from "howler";

/**
 * Chrome pairs Web Audio playback with the microphone's speaker while echo
 * cancellation is on, so a session can ignore the OS output (wired earphones)
 * and play through the built-in speakers. setSinkId on the playback context
 * pins the route. Browsers without it (Safari, including iOS) keep the OS route.
 */
type Sinkable = {
  setSinkId?: (sinkId: string) => Promise<void>;
};

const appliedSinks = new WeakMap<object, string>();
/**
 * One lookup for every playback chunk. `null` means "not looked up yet".
 * `{ id: null }` means we looked and Chrome had no named default device, so
 * later chunks must not call enumerateDevices() again.
 *
 * Cleared only when applyOutputSink is called with force (a devicechange, or
 * the mic opening). It survives a session ending. A learner who unplugs
 * earphones and starts another session without reloading keeps the previous
 * id until one of those force calls runs.
 */
let concreteDefaultLookup: { id: string | null } | null = null;

/**
 * Chrome labels the OS output as "Default - USB Earphones" on deviceId
 * "default", and also lists that hardware under its own id. Echo cancellation
 * can keep the virtual default on the built-in speakers, so prefer the
 * concrete id that matches the default's name.
 */
const concreteDefaultDeviceId = async (): Promise<string | null> => {
  if (concreteDefaultLookup) return concreteDefaultLookup.id;
  if (!navigator.mediaDevices?.enumerateDevices) {
    concreteDefaultLookup = { id: null };
    return null;
  }

  try {
    const devices = await navigator.mediaDevices.enumerateDevices();
    const outputs = devices.filter((device) => device.kind === "audiooutput");
    const virtualDefault = outputs.find(
      (device) => device.deviceId === "default"
    );
    const named = virtualDefault?.label.replace(/^default\s*-\s*/i, "").trim();
    if (!named) {
      concreteDefaultLookup = { id: null };
      return null;
    }

    const concrete = outputs.find(
      (device) =>
        device.deviceId !== "default" &&
        device.deviceId !== "communications" &&
        device.label === named
    );
    concreteDefaultLookup = { id: concrete?.deviceId ?? null };
    return concreteDefaultLookup.id;
  } catch {
    return null;
  }
};

const sinkCandidates = async (
  sinkId: string | null | undefined
): Promise<string[]> => {
  if (sinkId && sinkId !== "default") return [sinkId, ""];
  const concrete = await concreteDefaultDeviceId();
  // "" is the spec default, used where "default" is not a real sink id.
  return concrete ? [concrete, "default", ""] : ["default", ""];
};

export const applyOutputSink = async (
  target: object | null | undefined,
  sinkId: string | null | undefined,
  force = false
): Promise<void> => {
  if (!target) return;
  const setSinkId = (target as Sinkable).setSinkId;
  if (typeof setSinkId !== "function") return;

  if (force) {
    appliedSinks.delete(target);
    concreteDefaultLookup = null;
  }

  const candidates = await sinkCandidates(sinkId);
  const already = appliedSinks.get(target);
  if (already === candidates[0]) return;

  let lastError: unknown;
  for (const candidate of candidates) {
    if (candidate === already) return;
    try {
      await setSinkId.call(target, candidate);
      appliedSinks.set(target, candidate);
      return;
    } catch (error) {
      lastError = error;
    }
  }

  console.warn(
    "[audioOutput] setSinkId failed, leaving the browser output route:",
    lastError
  );
};

export const applyHowlerOutputSink = (
  sinkId: string | null | undefined,
  force = false
) => {
  const ctx = (Howler as { ctx?: AudioContext }).ctx;
  return applyOutputSink(ctx, sinkId, force);
};

/** Set the sink on a Howl's HTML audio nodes before playback starts. */
export const applyHowlSink = async (
  howl: object,
  sinkId: string | null | undefined
) => {
  const sounds =
    (howl as { _sounds?: Array<{ _node?: HTMLMediaElement | null }> })
      ._sounds ?? [];
  await Promise.all(
    sounds.map((sound) => applyOutputSink(sound._node, sinkId))
  );
};

export const playMediaOnSink = async (
  src: string,
  sinkId: string | null | undefined
) => {
  const audio = new Audio(src);
  await applyOutputSink(audio, sinkId);
  await audio.play();
};
